import { Checkbox } from 'components/Form/Checkbox/Checkbox';
import { PetAvatar } from 'components/Pet/Avatar/PetAvatar';
import { PetTypeChip } from 'components/Pet/TypeChip/PetTypeChip';
import { Tag } from 'components/UI/Tag/Tag';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { Pet, SavePet } from 'types/Pet';
import { getVaccineIssues, requiredVaccines } from 'utils/petUtils';

import { Button } from 'components/UI/Button/Button';
import classes from './PetOption.module.scss';

interface PetOptionProps {
    pet: Pet | SavePet;
    checked?: boolean;
    hideCheckbox?: boolean;
    /** Online Booking only: surface a tag when required vaccines are missing. */
    enforceRequirements?: boolean;
    onEdit?: () => void;
    onChange?: (checked: boolean) => void;
}

export const PetOption: React.FC<PetOptionProps> = props => {
    const marketplace = useMarketplace();
    const petRequiredVaccines = requiredVaccines(marketplace, props.pet.type.id);
    const vaccinesDisabled = petRequiredVaccines.length === 0;

    const petDescription = React.useMemo(() => {
        const breedName = props.pet.breed?.name ?? 'Not Defined';
        const petSize = props.pet.size.name;

        return `${breedName} - ${petSize}`;
    }, [props.pet]);

    const vaccineTagLabel = React.useMemo(() => {
        if (vaccinesDisabled) {
            return null;
        }

        const pluralize = (count: number, noun: string) =>
            `${count} ${noun}${count > 1 ? 's' : ''}`;

        if (props.enforceRequirements) {
            if (!marketplace.settings.requireVaccinesEnabled) {
                return null;
            }

            const { missing, expired } = getVaccineIssues(props.pet.vaccines, petRequiredVaccines);

            if (missing.length > 0 && expired.length > 0) {
                return 'Vaccines missing or expired';
            }
            if (missing.length > 0) {
                return `${pluralize(missing.length, 'Vaccine')} Missing`;
            }
            if (expired.length > 0) {
                return `${pluralize(expired.length, 'Vaccine')} Expired`;
            }

            return null;
        }

        const expiredCount = props.pet.vaccines.filter(
            vaccine => vaccine.status === 'Expired'
        ).length;

        return expiredCount > 0 ? `${pluralize(expiredCount, 'Vaccine')} Expired` : null;
    }, [
        props.pet,
        vaccinesDisabled,
        props.enforceRequirements,
        petRequiredVaccines,
        marketplace.settings.requireVaccinesEnabled
    ]);

    const togglePetHandler = () => {
        props.onChange?.(!props.checked);
    };

    return (
        <div className={classes.root}>
            <div className={classes.content}>
                <div className={classes.checkboxIconContainer}>
                    {!props.hideCheckbox && (
                        <Checkbox
                            checked={props.checked}
                            onChange={togglePetHandler}
                            className={classes.checkbox}
                        />
                    )}
                    <PetAvatar
                        image={props.pet.image?.src}
                        petType={props.pet.type}
                        onClick={togglePetHandler}
                    />
                </div>

                <div className={classes.petInfoContainer}>
                    <p className={classes.petName} onClick={togglePetHandler}>
                        {props.pet.name}
                        <PetTypeChip petType={props.pet.type} />
                    </p>
                    <p className={classes.petDescription}>{petDescription}</p>
                </div>

                <div className={classes.petActionsContainer}>
                    <Button size="small" variant="outlined" onClick={props.onEdit}>
                        Edit
                    </Button>
                </div>
            </div>

            {vaccineTagLabel && (
                <div className={classes.expiredVaccinesTagContainer}>
                    <Tag className={classes.expiredVaccinesTag}>{vaccineTagLabel}</Tag>
                </div>
            )}
        </div>
    );
};
